import { useState, useEffect, useCallback } from 'react';
import { whatsappAccountService } from '../../../services/api';

const useAccountsData = () => {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalAccounts, setTotalAccounts] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    verification: '',
    businessType: ''
  });
  const [refreshingAccounts, setRefreshingAccounts] = useState({});

  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await whatsappAccountService.getAccounts(
        page + 1, 
        rowsPerPage, 
        searchQuery,
        filters
      );
      
      setAccounts(response.data || []);
      setTotalAccounts(response.total || 0);
    } catch (err) {
      console.error('Error fetching accounts:', err);
      setError(err.response?.data?.error || 'Failed to fetch WhatsApp accounts');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchQuery, filters]);

  // Debounce search input
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchQuery(searchInput);
      setPage(0); // Reset to first page on search
    }, 500);
    
    return () => clearTimeout(timeoutId);
  }, [searchInput]);

  // Fetch accounts when dependencies change
  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const updateAccount = useCallback((accountId, updatedData) => {
    setAccounts(prev => 
      prev.map(account => 
        account._id === accountId 
          ? { ...account, ...updatedData }
          : account
      )
    );
  }, []);

  const removeAccount = useCallback((accountId) => {
    setAccounts(prev => prev.filter(account => account._id !== accountId));
    setTotalAccounts(prev => prev - 1);
  }, []);

  const addAccount = useCallback((newAccount) => {
    setAccounts(prev => [newAccount, ...prev]);
    setTotalAccounts(prev => prev + 1);
  }, []);

  const refreshAccount = useCallback(async (account) => {
    const accountId = account._id;
    
    try {
      setRefreshingAccounts(prev => ({ ...prev, [accountId]: true }));
      setError(null);

      const response = await whatsappAccountService.refreshAccount(accountId);

      if (response && response.success && response.data) {
        const updatedAccount = response.data;

        // Try to get analytics data
        try {
          const endDate = Math.floor(Date.now() / 1000);
          const startDate = endDate - (30 * 24 * 60 * 60);

          const analyticsResponse = await whatsappAccountService.getAccountAnalytics(
            accountId,
            { startDate, endDate, granularity: 'DAY' }
          );

          if (analyticsResponse?.data?.analytics?.data_points) {
            const totalMessages = analyticsResponse.data.analytics.data_points
              .reduce((sum, point) => sum + (point.sent || 0), 0);
            updatedAccount.messagesUsed = totalMessages;
          }
        } catch (analyticsErr) {
          console.error('Analytics fetch failed during refresh:', analyticsErr);
        }

        updateAccount(accountId, updatedAccount);
        return { success: true, message: 'Account refreshed successfully' };
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Error refreshing account:', err);
      const errorMessage = err.response?.data?.error || 'Failed to refresh account';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setRefreshingAccounts(prev => ({ ...prev, [accountId]: false }));
    }
  }, [updateAccount]);

  const bulkRefresh = useCallback(async (accountIds) => {
    const results = [];
    
    for (const accountId of accountIds) {
      const account = accounts.find(acc => acc._id === accountId);
      if (account) {
        const result = await refreshAccount(account);
        results.push({ accountId, ...result });
      }
    }
    
    return results;
  }, [accounts, refreshAccount]);

  const handlePageChange = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  const handleSearchChange = useCallback((event) => {
    setSearchInput(event.target.value);
  }, []);

  const handleFilterChange = useCallback((filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
    setPage(0);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({
      status: '',
      verification: '',
      businessType: ''
    });
    setSearchInput('');
    setPage(0);
  }, []);

  return {
    // Data
    accounts,
    loading,
    error,
    totalAccounts,
    refreshingAccounts,
    
    // Pagination
    page,
    rowsPerPage,
    handlePageChange,
    handleRowsPerPageChange,
    
    // Search and filters
    searchInput,
    searchQuery,
    filters,
    handleSearchChange,
    handleFilterChange,
    clearFilters,
    
    // Actions
    fetchAccounts,
    updateAccount,
    removeAccount,
    addAccount,
    refreshAccount,
    bulkRefresh,
    
    // State setters
    setError,
    setLoading
  };
};

export default useAccountsData;